import React from 'react';
import { DOOR_W } from './RoomPicker';
import { shareLink, roomLinkFor, sharePageFor, pageUrl, shareTitleFor } from '../../utils/shareLink'; // 🔗 10-01 event links

/**
 * 🗓️ CalendarDoor (09-19, HOUSE_CALENDAR_OUTINGS_SPEC; Nina's D2 ruling: the
 * calendar lives behind a door in the message box, and that door carries the
 * AGENDA list).
 *
 *  · CalendarDoorButton — the sibling of RoomPicker.RoomDoorButton: same size,
 *    same spot in every room, so the two read as one pair. Its tiny label IS
 *    the advert ("Oct 31" → "Sat" → "tmrw" → "today" → "now"); no red dots, no
 *    badge counts.
 *  · CalendarAgenda — the sheet. A plain list, because nobody should have to
 *    decode a month grid at 380px (the month grid is the PARKED wall calendar).
 *
 * Pure presentation: every string arrives ready from utils/calendarView, every
 * action is a callback. Payload-gated by the CALLER (house.calendar is absent
 * until HOUSE_CALENDAR_ENABLED), so every other space is byte-identical.
 * ⚠️ This frontend has NO global box-sizing: every padded box sets it.
 *
 * 🔗 09-23 (MOVIE_NIGHT_UX_HANDOFF §3.9, Nina's ruling 17): a "Share" chip
 * beside "Go to …" on every row that has a room. It hands out the deep link
 * `/game-step?gameId=…&room=<id>&view=peek` (the link already waits at the
 * door, `8ff338e2c`): navigator.share on phones, the clipboard elsewhere,
 * `onToast('Link copied')` when the caller gives one. `gameId` is a prop
 * (the agenda never knew the game); absent, the page's own ?gameId= is read.
 *
 * 🔗 10-01 event links (HOUSE_OCTOBER_UIUX_BATCH_SPEC_2026_10_01 §3.5, Nina's
 * R14): every featured house/sky night that has a share page gets a small
 * TEXT button, "Invite", in a third grid column (the row never grows on a
 * phone). It hands out https://myspaice.ai/e/<slug>.html, a page with that
 * night's own picture (utils/shareLink.js, ONE share path). Resident plans
 * (`by`) and engine surprises keep the room chip. No 🔗 anywhere (R12/R14):
 * the room chip now reads "Share".
 *
 * 🗓️ 09-29: both glyphs here are the SPIRAL calendar. The tear-off one is a
 * page that says JUL 17 on Apple, so the door's "Sat" read as "Saturday the
 * 17th" and the sheet's head read as a date (Nina's 09-29 screenshot). The
 * composer's own door draws its leaf with the real day (ComposerDoors.js).
 * A ROW's own glyph can be the tear-off too: the backend's calendar falls back
 * to it (houseCalendar.js, e.g. "Clearing the air"), and so does
 * utils/calendarView.agendaRows. In the same screenshot it sat right beside
 * "Tue, Sep 29" reading JUL 17, so the sheet swaps it at render.
 */
const TEAR_OFF = /\u{1F4C5}\u{FE0F}?/gu;
export const calendarGlyph = (e) => (typeof e === 'string' ? e.replace(TEAR_OFF, '\u{1F5D3}\u{FE0F}') : e);
const LINE = 'rgba(122,96,60,0.55)';
const GOLD = '#e9b565';
const YOU = '#ffd98a';

export function CalendarDoorButton({ text = '', hot = false, title = 'House calendar', open = false, onToggle }) {
  const lit = open || hot;
  return (
    <button type="button" data-calendar-door="1"
      aria-label={title} aria-haspopup="dialog" aria-expanded={open} title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (onToggle) onToggle(); }}
      style={{
        flex: `0 0 ${DOOR_W}px`, width: DOOR_W, minHeight: 40, boxSizing: 'border-box', padding: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
        borderRadius: 4, cursor: 'pointer', font: 'inherit', fontSize: 16, lineHeight: 1,
        background: lit ? 'rgba(233,181,101,0.16)' : 'rgba(0,0,0,0.45)',
        border: `1px solid ${lit ? GOLD : LINE}`, color: hot ? YOU : GOLD,
      }}>
      <span aria-hidden="true">🗓️</span>
      {text && (
        <span aria-hidden="true" style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{text}</span>
      )}
    </button>
  );
}

/** The share link for a room, or null without a game or a room. */
export function roomShareLink(gameId, room) {
  if (typeof window === 'undefined') return null;
  return roomLinkFor(gameId, room); // 🔗 10-01 event links: one builder (same link as 09-23)
}
const urlGameId = () => { try { return new URLSearchParams(window.location.search).get('gameId'); } catch (e) { return null; } };
const roomOf = (r) => (r && (r.room || (r.go && r.go.room))) || null;
// 🔗 10-01 event links: the row's own night page, or null. Rows carry day + kind
// (or the agenda key `${day}|${kind}|${i}`); quiet rows and plans never invite.
export function invitePageOf(r) {
  if (!r || r.quiet || r.by) return null;
  const [kday, kkind] = String(r.key || '').split('|');
  return sharePageFor({ day: r.day || kday, kind: r.kind || kkind });
}

/** navigator.share where it exists (phones), else the clipboard + a toast.
 *  A cancelled share sheet is not an error and says nothing.
 *  🔗 10-01 event links: the ONE share path lives in utils/shareLink. */
export async function shareRoomLink(url, label, onToast) {
  const res = await shareLink(url, { title: label || 'The Claude House', onToast });
  return res === 'shared' || res === 'copied';
}

// 🔥 10-03 game nights: `onTale` (optional) = what the campfire row's "/tale" text chip does (the
// page seeds the composer with "/tale "). The chip draws only on a row that carries `tin.chip`
// (calendarView.campfireTin: the tin is open) AND when the caller gives onTale.
export function CalendarAgenda({ rows, onClose, onGo, onVote, gameId = null, onToast = null, onTale = null }) {
  const list = Array.isArray(rows) ? rows : [];
  const gid = gameId || urlGameId();
  return (
    <div data-calendar-agenda="1" role="dialog" aria-label="House calendar"
      style={{
        background: '#231a12', border: `1px solid ${LINE}`, borderRadius: 10, padding: 10,
        boxSizing: 'border-box', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 4,
        boxShadow: '0 -10px 40px rgba(0,0,0,0.6)',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <b style={{ color: GOLD, fontWeight: 900, fontSize: 13 }}>🗓️ Coming up at the house</b>
        <button type="button" aria-label="close" onClick={onClose}
          style={{ font: 'inherit', fontSize: 12, padding: '1px 7px', borderRadius: 6, background: '#2c2117', border: `1px solid ${LINE}`, color: '#efe4d2', cursor: 'pointer' }}>
          ✕
        </button>
      </div>
      {list.length === 0 && (
        <div style={{ fontSize: 12.5, color: '#a8977f', padding: '6px 2px' }}>Nothing on the calendar yet.</div>
      )}
      {list.map((r) => {
        const invite = invitePageOf(r); // 🔗 10-01 event links
        const roomLink = invite ? null : roomShareLink(gid, roomOf(r)); // the Invite carries the room's way in
        return (
        <div key={r.key} data-calendar-row={r.next ? 'next' : '1'}
          style={{
            display: 'grid', gridTemplateColumns: invite ? '26px minmax(0,1fr) auto' : '26px minmax(0,1fr)', gap: 8, alignItems: 'center',
            padding: '6px 6px', borderRadius: 8, boxSizing: 'border-box',
            border: `1px solid ${r.next ? YOU : 'transparent'}`,
            background: r.next ? 'rgba(255,217,138,0.06)' : 'transparent',
            opacity: r.quiet ? 0.78 : 1,
          }}>
          <span aria-hidden="true" style={{ fontSize: 18, textAlign: 'center', lineHeight: 1 }}>{calendarGlyph(r.emoji)}</span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 13, color: r.next ? YOU : '#efe4d2', overflowWrap: 'anywhere' }}>{r.label}</span>
            <span style={{ fontSize: 11.5, color: '#a8977f' }}>{r.dateLine}</span>
            {r.by && <span style={{ fontSize: 11.5, fontStyle: 'italic', color: '#c9b892' }}>{r.by}</span>}
            {/* 🚪 the house is OUT and you are not there: the one tap that follows it
                🔗 09-23: + Share beside it on any row with a room
                🔗 10-01 event links: the room link is computed once (null on a row with an Invite) */}
            {((r.go && onGo) || roomLink) && (
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 4, minWidth: 0 }}>
                {r.go && onGo && (
                  <button type="button" data-calendar-go={r.go.room}
                    onClick={() => { onGo(r.go.room); if (onClose) onClose(); }}
                    style={{
                      boxSizing: 'border-box', maxWidth: '100%',
                      font: 'inherit', fontSize: 12.5, fontWeight: 800, padding: '5px 10px', borderRadius: 7,
                      background: GOLD, border: `1px solid ${GOLD}`, color: '#231a12', cursor: 'pointer',
                      overflowWrap: 'anywhere', textAlign: 'left',
                    }}>
                    Go to {r.go.label}
                  </button>
                )}
                {/* 🔗 10-01 event links: R12/R14, the chip is a word ("Share"), never 🔗 (it reads as a paperclip) */}
                {roomLink && (
                  <button type="button" data-calendar-share={roomOf(r)} title="Share a link to this room"
                    onClick={() => { shareRoomLink(roomLink, r.label, onToast); }}
                    style={{
                      boxSizing: 'border-box', font: 'inherit', fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 999,
                      background: 'transparent', border: `1px solid ${LINE}`, color: '#efe4d2', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                    Share
                  </button>
                )}
              </span>
            )}
            {r.here && <span style={{ fontSize: 11.5, fontWeight: 800, color: YOU }}>📍 You are here</span>}
            {/* 🔥 10-03 game nights: the campfire's tin (count) + a TEXT chip, right-aligned (the composer ruling: words, never an icon) */}
            {r.tin && (
              <span data-calendar-tin="1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 4, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: '#c9b892', overflowWrap: 'anywhere' }}>{r.tin.text}</span>
                {r.tin.chip && onTale && (
                  <button type="button" data-calendar-tale="1" title="Drop a sealed tale in the tin"
                    onClick={() => { onTale(); if (onClose) onClose(); }}
                    style={{
                      boxSizing: 'border-box', marginLeft: 'auto', minHeight: 32, font: 'inherit', fontSize: 12, fontWeight: 800, padding: '4px 12px', borderRadius: 999,
                      background: 'transparent', border: `1px solid ${LINE}`, color: YOU, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                    {r.tin.chip}
                  </button>
                )}
              </span>
            )}
            {/* 🎬🍿 the film ballot: one vote each, changeable until it closes */}
            {r.ballot && (
              <span data-calendar-ballot={r.ballot.open ? 'open' : 'closed'} style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 5, minWidth: 0 }}>
                <span style={{ fontSize: 11.5, color: '#c9b892' }}>{r.ballot.note}</span>
                {r.ballot.films.map((f) => (
                  <span key={f.idx} data-ballot-film={f.idx}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 8px', borderRadius: 8, boxSizing: 'border-box', minWidth: 0,
                      background: '#2c2117', border: `1px solid ${f.winner ? GOLD : f.mine ? YOU : LINE}`,
                    }}>
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <b style={{ fontSize: 13, color: '#efe4d2', overflowWrap: 'anywhere' }}>{f.winner ? '🎬 ' : ''}{f.title}</b>
                      <span style={{ fontSize: 11, color: '#a8977f', whiteSpace: 'nowrap' }}>{f.meta}</span>
                    </span>
                    <span style={{ fontSize: 12, color: '#c9b892', overflowWrap: 'anywhere' }}>{f.pitch}</span>
                    <span aria-hidden="true" style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: `${f.pct}%`, background: GOLD, borderRadius: 2 }} />
                    </span>
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11.5, color: '#efe4d2', fontVariantNumeric: 'tabular-nums' }}>
                        {f.votes} {f.votes === 1 ? 'vote' : 'votes'}{f.winner ? ' · tonight' : ''}
                      </span>
                      {r.ballot.open && onVote && (
                        <button type="button" data-ballot-vote={f.idx} aria-pressed={f.mine} onClick={() => onVote(f.cmd)}
                          style={{
                            font: 'inherit', fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 999, cursor: 'pointer', boxSizing: 'border-box',
                            background: f.mine ? YOU : 'transparent', color: f.mine ? '#231a12' : YOU, border: `1px solid ${YOU}`,
                          }}>
                          {f.mine ? 'Your vote' : 'Vote'}
                        </button>
                      )}
                    </span>
                  </span>
                ))}
              </span>
            )}
          </span>
          {/* 🔗 10-01 event links: the night's own page (its picture in every preview) */}
          {invite && (
            <button type="button" data-calendar-invite={invite.slug} title="Invite someone: copy this night's link"
              onClick={() => { shareLink(pageUrl(invite), { title: shareTitleFor(invite, r.label), onToast }); }}
              style={{
                boxSizing: 'border-box', alignSelf: 'center', font: 'inherit', fontSize: 11.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
                background: 'transparent', border: `1px solid ${LINE}`, color: GOLD, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              Invite
            </button>
          )}
        </div>
        );
      })}
    </div>
  );
}
